import React, { FC, useEffect } from 'react';
import styles from '../journey-explorer.module.scss';
import {
	JourneyAttribute,
	JourneyFilterState,
	JourneyMeta,
	JourneySegment,
} from '../../../../models/journey-explorer/journey-explorer';
import { InteractionInfo } from '../journey-map-layout';
import SegmentBreakdown from './segment-breakdown';
import TimeBetween from './time-between';
import SegmentCustomers from './segment-customers';
import CustomerTimeline from './customer-timeline';

export type DrawerView =
	| { mode: 'breakdown' | 'time' | 'customers'; segment: JourneySegment; title: string }
	| { mode: 'timeline'; customerId: string };

const HEADINGS: Record<DrawerView['mode'], string> = {
	breakdown: 'Who are they',
	time: 'Time between steps',
	customers: 'Customers',
	timeline: 'Customer timeline',
};

interface ExploreDrawerProps {
	views: DrawerView[];
	meta: JourneyMeta;
	filters: JourneyFilterState;
	lookup: Map<string, InteractionInfo>;
	onPush: (view: DrawerView) => void;
	onBack: () => void;
	onClose: () => void;
	onKeep: (attribute: JourneyAttribute, value: string) => void;
}

const ExploreDrawer: FC<ExploreDrawerProps> = ({ views, meta, filters, lookup, onPush, onBack, onClose, onKeep }) => {
	const view = views[views.length - 1];

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [onClose]);

	if (!view) {
		return null;
	}

	const title = view.mode === 'timeline' ? view.customerId : view.title;

	return (
		<aside className={styles.drawer} aria-label={HEADINGS[view.mode]}>
			<div className={styles.drawer_header}>
				{views.length > 1 && (
					<button type="button" className={styles.panel_close} aria-label="Back" onClick={onBack}>
						‹
					</button>
				)}
				<div className={styles.drawer_titles}>
					<div className={styles.drawer_heading}>{HEADINGS[view.mode]}</div>
					<div className={styles.drawer_title} title={title}>
						{title}
					</div>
				</div>
				<button type="button" className={styles.panel_close} aria-label="Close" onClick={onClose}>
					✕
				</button>
			</div>
			{views.map((item, index) => (
				<div
					key={`${index}:${JSON.stringify(item)}`}
					className={styles.drawer_body}
					hidden={index !== views.length - 1}
				>
					{item.mode === 'breakdown' && (
						<SegmentBreakdown meta={meta} filters={filters} segment={item.segment} onKeep={onKeep} />
					)}
					{item.mode === 'time' && <TimeBetween filters={filters} segment={item.segment} />}
					{item.mode === 'customers' && (
						<SegmentCustomers
							filters={filters}
							segment={item.segment}
							onOpenCustomer={(customerId) => onPush({ mode: 'timeline', customerId })}
						/>
					)}
					{item.mode === 'timeline' && <CustomerTimeline customerId={item.customerId} lookup={lookup} />}
				</div>
			))}
		</aside>
	);
};

export default ExploreDrawer;
